/**
 * useLiveEvents — Global singleton polling hook.
 * Polls /api/public/events/live every 30s.
 * All PickCards share the SAME poll — no N+1 requests.
 */
import { useState, useEffect } from "react";

const POLL_INTERVAL = 30_000; // 30 seconds

// Module-level singleton state
let _events = [];
let _listeners = new Set();
let _timer = null;
let _lastFetch = 0;

function notifyAll() {
  _listeners.forEach(fn => fn([..._events]));
}

async function fetchLive() {
  try {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    const res = await fetch(`${base}/api/public/events/live`);
    if (!res.ok) return;
    const data = await res.json();
    _events = data.events || [];
    _lastFetch = Date.now();
    notifyAll();
  } catch {
    // silent — no live data is fine
  }
}

function startPolling() {
  if (_timer) return;
  fetchLive();
  _timer = setInterval(fetchLive, POLL_INTERVAL);
}

function stopPolling() {
  if (_listeners.size > 0) return; // other components still listening
  if (_timer) { clearInterval(_timer); _timer = null; }
}

export function useLiveEvents() {
  const [events, setEvents] = useState(_events);

  useEffect(() => {
    _listeners.add(setEvents);
    startPolling();
    // If data is stale (>30s old), refetch immediately
    if (Date.now() - _lastFetch > POLL_INTERVAL) fetchLive();
    return () => {
      _listeners.delete(setEvents);
      stopPolling();
    };
  }, []);

  return events;
}

/**
 * Try to match a pick (home_team, away_team) to a live ESPN event.
 * Uses normalized name comparison to handle minor differences.
 */
function normalize(str) {
  return (str || "")
    .toLowerCase()
    .replace(/\b(fc|cf|sc|ac|rc|sd|cd|rcd|real|atletico|atlético|deportivo|united|city|city fc|sporting|the|los|las|new|san|de|del|la|el)\b/g, "")
    .replace(/[^a-z0-9]/g, "")
    .trim();
}

function nameMatches(pickName, espnName) {
  const a = normalize(pickName);
  const b = normalize(espnName);
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

export function findLiveMatch(liveEvents, homeTeam, awayTeam) {
  return liveEvents.find(ev =>
    nameMatches(homeTeam, ev.home_team) && nameMatches(awayTeam, ev.away_team)
  ) || liveEvents.find(ev =>
    nameMatches(homeTeam, ev.away_team) && nameMatches(awayTeam, ev.home_team)
  ) || null;
}
